import { onLCP, onINP, onCLS, onTTFB, onFCP, type Metric } from "web-vitals";

export type VitalEntry = {
  name: string;
  value: number;
  rating: Metric["rating"];
  at: number;
};

export type SlowRequestEntry = {
  label: string;
  ms: number;
  at: number;
  detail?: string;
  error?: boolean;
};

type Listener = () => void;

const listeners = new Set<Listener>();
const vitals = new Map<string, VitalEntry>();
const slowRequests: SlowRequestEntry[] = [];

/** Anything above this is worth surfacing as a Live Replay regression signal. */
export const SLOW_REQUEST_MS = 800;
const MAX_SLOW_ENTRIES = 50;

const emit = () => listeners.forEach((l) => l());

export const subscribePerf = (l: Listener) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export const getVitals = (): VitalEntry[] =>
  [...vitals.values()].sort((a, b) => a.name.localeCompare(b.name));

export const getSlowRequests = (): SlowRequestEntry[] => slowRequests;

let started = false;

/** Register Web Vitals collection once per page load. */
export const initWebVitals = () => {
  if (started || typeof window === "undefined") return;
  started = true;

  const report = (m: Metric) => {
    vitals.set(m.name, {
      name: m.name,
      value: Math.round(m.name === "CLS" ? m.value * 1000 : m.value),
      rating: m.rating,
      at: Date.now(),
    });
    if (m.rating !== "good") {
      // eslint-disable-next-line no-console
      console.warn(`[web-vitals] ${m.name} ${m.value.toFixed(3)} (${m.rating})`);
    }
    emit();
  };

  onLCP(report);
  onINP(report);
  onCLS(report);
  onFCP(report);
  onTTFB(report);
};

/** Time an async pipeline call; slow or failing calls are recorded and logged. */
export const trackRequest = async <T>(
  label: string,
  fn: () => Promise<T>,
  detail?: string,
): Promise<T> => {
  const t0 = performance.now();
  let failed = false;
  try {
    return await fn();
  } catch (e) {
    failed = true;
    throw e;
  } finally {
    const ms = Math.round(performance.now() - t0);
    if (ms >= SLOW_REQUEST_MS || failed) {
      slowRequests.unshift({ label, ms, at: Date.now(), detail, error: failed });
      if (slowRequests.length > MAX_SLOW_ENTRIES) slowRequests.length = MAX_SLOW_ENTRIES;
      // eslint-disable-next-line no-console
      console.warn(`[live-replay] slow request "${label}" ${ms}ms`, detail ?? "");
      emit();
    }
  }
};
